import React, { useEffect, useRef, useState } from "react";
import styles from "./SetupDropdown.module.css";
import { motion, AnimatePresence } from "framer-motion";
import { HiXMark, HiCog6Tooth } from "react-icons/hi2";
import Button from '../ui/Button';

/**
 * Reusable setup dropdown menu with customizable tabs and optional custom className.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onConfirm: () => void
 * - tabs: [{ id: string, label: string, content: ReactNode }]
 * - defaultTabId?: string
 * - title?: string
 * - className?: string (optional)
 */
export default function SetupDropdown({
  isOpen,
  onClose,
  onConfirm,
  tabs = [],
  defaultTabId,
  title = "Setup",
  className = "",
}) {
  const [activeTab, setActiveTab] = useState(defaultTabId || (tabs[0]?.id ?? ""));
  const dropdownRef = useRef(null);

  // Escape key close
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Click-away close
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose]);

  // Reset to default when opened
  useEffect(() => {
    if (isOpen && defaultTabId) {
      setActiveTab(defaultTabId);
    }
  }, [isOpen, defaultTabId]);

  const activeContent = tabs.find((t) => t.id === activeTab)?.content || null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dropdownRef}
          className={`${styles.dropdownWrap} ${className}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}>
          {/* Header */}
          <header className={styles.header}>
            {/*<div className={styles.title}>
              <HiCog6Tooth size={18} />
              {title}
            </div>*/}
            <Button onClick={onClose} className={styles.closeBtn} aria-label="Close setup">
              <HiXMark size={24} />
            </Button>
          </header>

          {/* Tabs */}
          {tabs.length > 0 && (
            <div className={styles.tabs}>
              {tabs.map((t) => (
                <Button variant={activeTab === t.id ? "primary" : "ghost"}
                  key={t.id}
                  className={`${styles.tabBtn} ${
                    activeTab === t.id ? styles.tabActive : ""
                  }`}
                  onClick={() => setActiveTab(t.id)}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          )}

          {/* Content */}
          <motion.div
            key={activeTab}
            className={styles.tabContent}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {activeContent}
          </motion.div>

          {/* Footer */}
          <footer className={styles.footer}>
            <Button className={styles.okBtn} onClick={() => onConfirm(activeTab)}>
              OK
            </Button>
          </footer>
        </motion.div>
      )}
    </AnimatePresence>
  );
}